import React, { useState, useEffect, useRef } from 'react';
import useTranslation from '../../hooks/useTranslation';
import { doctorStartSession, doctorSaveEmotions, doctorAddNote } from '../api';
import { getPredictEndpoint } from '../../utils/predictApi';
import { unwrapApiResponse, parseTop5Percentages } from '../../utils/emotionMapping';
import { averageSegments } from '../../utils/doctorDashboardUtils';
import { EMOTION_KEYS } from '../../utils/emotionConstants';
import { authFetch, authHeaders } from '../../utils/authFetch';

const API_HOST = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:8000' : 'https://app.neurolonic.com/api');

const callPredictAPI = async (audioBlob, retryCount = 0) => {
  try {
    if (audioBlob.size < 100) {
      return { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0, burnout_score: 0, _dataSource: 'silence_detected' };
    }

    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.wav');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const response = await fetch(getPredictEndpoint(), {
      method: 'POST',
      body: formData,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    const res = unwrapApiResponse(result);
    const top5String = res.top_5 || '';
    const scores = parseTop5Percentages(top5String);

    const joy = Math.max(0, Math.min(100, scores.happy ?? 0));
    const sadness = Math.max(0, Math.min(100, scores.sad ?? 0));
    const anger = Math.max(0, Math.min(100, scores.angry ?? 0));
    const stress = Math.max(0, Math.min(100, scores.fear ?? 0));
    const hurt = Math.max(0, Math.min(100, scores.disgust ?? 0));
    const burnout = Math.round(((scores.angry || 0) + (scores.sad || 0) + (scores.fear || 0) + (scores.disgust || 0)) / 4) || 0;

    return { joy, stress, sadness, anger, hurt, burnout_score: burnout, _dataSource: 'predict_api' };
  } catch (error) {
    console.error('PREDICT API ERROR:', error);

    if (retryCount < 2 && (
      error.name === 'AbortError' ||
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network Error')
    )) {
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return callPredictAPI(audioBlob, retryCount + 1);
    }

    return { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0, burnout_score: 0, _dataSource: 'error', _error: error.message };
  }
};

export default function SessionPanel({ patient, isOpen, onClose, onSessionSaved }) {
  const { t } = useTranslation();
  const [micClicks, setMicClicks] = useState(0);
  const [segmentRecording, setSegmentRecording] = useState(false);
  const [segmentResults, setSegmentResults] = useState([]);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [finalResults, setFinalResults] = useState(null);
  const [timer, setTimer] = useState(0);
  const [running, setRunning] = useState(false);
  const [sessionNote, setSessionNote] = useState('');
  const [clinicalAssessment, setClinicalAssessment] = useState('Stable');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTimer(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    return () => {
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      } catch (_) { }
    };
  }, []);

  const resetState = () => {
    setMicClicks(0);
    setSegmentResults([]);
    setSessionComplete(false);
    setFinalResults(null);
    setTimer(0);
    setRunning(false);
    setSessionNote('');
    setClinicalAssessment('Stable');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });

        if (micClicks > 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        setIsAnalyzing(true);
        const result = await callPredictAPI(audioBlob);
        setIsAnalyzing(false);

        setSegmentResults(prev => {
          const next = [...prev, result];
          if (next.length === 5) {
            const avg = averageSegments(next);
            if (avg) {
              setFinalResults(avg);
            }
            setSessionComplete(true);
            setRunning(false);
          }
          return next;
        });

        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setSegmentRecording(true);

      setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
          setSegmentRecording(false);
        }
      }, 8000);
    } catch (error) {
      console.error('Recording failed:', error);
      alert('Microphone access denied or recording failed. Please check permissions.');
      setSegmentRecording(false);
    }
  };

  const handleStartRecording = () => {
    if (!patient) return;
    if (micClicks >= 5 || segmentRecording) return;

    if (micClicks === 0) {
      setTimer(0);
      setRunning(true);
      setSegmentResults([]);
      setSessionComplete(false);
      setFinalResults(null);
    }

    setMicClicks(c => c + 1);
    startRecording();
  };

  const handleSaveSession = async () => {
    if (!patient || !finalResults) {
      alert('Please complete all 5 recordings before saving');
      return;
    }

    setSaving(true);
    try {
      const sessionRes = await doctorStartSession(patient.id, Math.max(1, timer));
      if (sessionRes.status_code !== 200) {
        throw new Error('Failed to start session');
      }
      const sessionId = sessionRes.data.session_id;

      const emotionsRes = await doctorSaveEmotions({
        doctor_patient_id: patient.id,
        session_id: sessionId,
        joy: finalResults.emotions?.joy || 0,
        stress: finalResults.emotions?.stress || 0,
        sadness: finalResults.emotions?.sadness || 0,
        anger: finalResults.emotions?.anger || 0,
        burnout_score: finalResults.burnout_score || 0,
        doctor_assessment: clinicalAssessment
      });

      if (emotionsRes.status_code !== 200) {
        throw new Error('Failed to save emotion data');
      }

      if (sessionNote.trim()) {
        await doctorAddNote(sessionId, sessionNote.trim());
      }

      alert('Session saved successfully!');
      resetState();
      onSessionSaved();
      onClose();

      const id = localStorage.getItem('neuro_user_id');
      if (id) {
        setTimeout(() => {
          authFetch(`${API_HOST}/user/details?user_id=${id}`, { headers: authHeaders() })
            .then(res => res.json())
            .then(data => {
              if (data.status_code === 200 && data.data) {
                try {
                  const info = typeof data.data.info === 'string' ? JSON.parse(data.data.info) : data.data.info;
                  if (info && info.hospital_id && onSessionSaved) {
                    onSessionSaved();
                  }
                } catch (e) {
                  console.error('Error parsing user info:', e);
                }
              }
            })
            .catch(err => console.error('Error fetching user details:', err));
        }, 500);
      }
    } catch (error) {
      console.error('Error saving session:', error);
      alert(t('Error saving session. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !patient) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{t('Session with')} {patient.name}</h2>
        <div className="session-controls">
          {!sessionComplete && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <p><strong>{t('Progress')}:</strong> {micClicks} / 5 {t('recordings completed')}</p>
                {running && <p>{t('Total time')}: {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}</p>}
              </div>

              {segmentRecording && (
                <div className="recording-status">
                  <p>🔴 {t('Recording segment')} {micClicks}... (8 {t('seconds')})</p>
                </div>
              )}

              {isAnalyzing && !segmentRecording && (
                <div className="analyzing-status">
                  <p>⏳ {t('Analyzing segment')} {micClicks}...</p>
                </div>
              )}

              {!segmentRecording && !isAnalyzing && (
                <button
                  className="btn-primary"
                  onClick={handleStartRecording}
                  disabled={micClicks >= 5}
                >
                  {micClicks === 0
                    ? t('Start Recording (1/5)')
                    : micClicks < 5
                      ? t('Record Next Segment') + ` (${micClicks + 1}/5)`
                      : t('All Recordings Complete')}
                </button>
              )}
            </>
          )}

          {sessionComplete && finalResults && (
            <div className="emotion-results">
              <h3>Emotion Analysis Results:</h3>
              <div style={{ marginBottom: '16px' }}>
                <p><strong>Joy:</strong> {finalResults.emotions?.joy?.toFixed(1)}%</p>
                <p><strong>Stress:</strong> {finalResults.emotions?.stress?.toFixed(1)}%</p>
                <p><strong>Sadness:</strong> {finalResults.emotions?.sadness?.toFixed(1)}%</p>
                <p><strong>Anger:</strong> {finalResults.emotions?.anger?.toFixed(1)}%</p>
                <p><strong>Burnout Score:</strong> {finalResults.burnout_score || 0}</p>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#374151' }}>
                  Clinical Assessment: *
                </label>
                <select
                  value={clinicalAssessment}
                  onChange={(e) => setClinicalAssessment(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                    color: '#111827'
                  }}
                  required
                >
                  <option value="Stable">Stable</option>
                  <option value="Moderate Risk">Moderate Risk</option>
                  <option value="High Risk">High Risk</option>
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#374151' }}>
                  Clinical Assessment Note:
                </label>
                <textarea
                  placeholder="Enter your clinical assessment notes and observations..."
                  value={sessionNote}
                  onChange={(e) => setSessionNote(e.target.value)}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>
              <div className="modal-actions">
                <button
                  className="btn-primary"
                  onClick={handleSaveSession}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Session'}
                </button>
                <button onClick={handleClose}>{t('Cancel')}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
